/** Private content review (Waqas 2026-05-15). Sits under "Memso
 *  noticed" on Today. Lists scope revisions the private-scope watcher
 *  made: explicit / intimate / private-health content it moved into
 *  Private scope, plus "re-confirm?" prompts for content the user had
 *  locked to another scope.
 *
 *  This panel is itself scope=private. It renders ONLY when the
 *  active view includes private (Private view or Everything). The
 *  backend enforces the same gate; this is the matching client gate
 *  so the panel never flashes on a non-private view. Previews are
 *  redacted (a short masked snippet) until the user reveals them. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldAlert, Lock, Eye, EyeOff, Check, Loader2 } from "lucide-react";
import { api, type PrivateRevision } from "../lib/api";
import { useView } from "../lib/viewContext";

const REASON_LABEL: Record<string, string> = {
  adult_domain: "Adult-content sender",
  nsfw_content: "Explicit content",
  intimate_health: "Private health detail",
  personal_intimate: "Personal / intimate",
};

const RECLASSIFY_SCOPES = [
  "personal",
  "family",
  "social",
  "work",
  "business",
] as const;

export function PrivateContentReviewPanel() {
  const { activeView } = useView();
  const privateVisible = useMemo(() => {
    if (!activeView) return false;
    return activeView.isEverything || activeView.scopes.includes("private");
  }, [activeView]);

  const [items, setItems] = useState<PrivateRevision[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!privateVisible) {
      setLoaded(true);
      return;
    }
    api
      .privateReview()
      .then((d) => {
        setItems(d.gated ? [] : d.items);
        setLoaded(true);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoaded(true);
      });
  }, [privateVisible]);
  useEffect(load, [load]);

  if (!privateVisible) return null;
  if (!loaded) return null;
  if (items.length === 0 && !error) return null;

  const unreviewed = items.filter((i) => !i.reviewedAt).length;

  const keep = async (packetId: string) => {
    if (busy) return;
    setBusy(packetId);
    try {
      await api.privateReviewKeep(packetId);
      setItems((prior) =>
        prior.map((i) =>
          i.packetId === packetId
            ? { ...i, reviewedAt: new Date().toISOString(), userAction: "kept_private" }
            : i,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const reclassify = async (packetId: string, scope: string) => {
    if (busy || !scope) return;
    setBusy(packetId);
    try {
      await api.privateReviewReclassify(packetId, scope);
      setItems((prior) =>
        prior.map((i) =>
          i.packetId === packetId
            ? {
                ...i,
                reviewedAt: new Date().toISOString(),
                userAction: "reclassified",
                reclassifiedTo: scope,
              }
            : i,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const markAll = async () => {
    if (busy) return;
    setBusy("__all__");
    try {
      await api.privateReviewMarkAllReviewed();
      const now = new Date().toISOString();
      setItems((prior) =>
        prior.map((i) =>
          i.reviewedAt
            ? i
            : { ...i, reviewedAt: now, userAction: i.userAction ?? "kept_private" },
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const toggleReveal = (id: string) =>
    setRevealed((prior) => {
      const next = new Set(prior);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section
      aria-label="Private content review"
      className="glass rounded-2xl p-4 sm:p-5 border border-rose-200/70 dark:border-rose-900/40 backdrop-blur space-y-3"
    >
      <header className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-rose-600 dark:text-rose-300">
          <ShieldAlert className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-sm font-bold text-slate-900 dark:text-slate-100">
            Private content review ({unreviewed})
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Memso moved these into Private scope. Keep private, or reclassify if Memso got it wrong.
          </p>
        </div>
        {unreviewed > 0 && (
          <button
            type="button"
            onClick={markAll}
            disabled={!!busy}
            className="text-xs font-semibold px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700/40 disabled:opacity-50"
          >
            Mark all reviewed
          </button>
        )}
      </header>
      {error && (
        <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>
      )}
      <ul className="space-y-2">
        {items.map((rev) => {
          const isRevealed = revealed.has(rev.id);
          return (
            <li
              key={rev.id}
              className={`rounded-lg p-3 flex flex-col sm:flex-row sm:items-start gap-3 ${
                rev.reviewedAt
                  ? "bg-slate-50/60 dark:bg-slate-800/30 opacity-70"
                  : "bg-rose-50/60 dark:bg-rose-950/20"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-200">
                    {REASON_LABEL[rev.reason] ?? rev.reason}
                  </span>
                  {rev.status === "pending" && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-200">
                      Re-confirm
                    </span>
                  )}
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                    {rev.wasScope ? `${rev.wasScope} → ` : ""}
                    {rev.reclassifiedTo ?? rev.nowScope}
                    {rev.sourceKind ? ` · ${rev.sourceKind}` : ""}
                  </span>
                </div>
                <div className="mt-1.5 flex items-start gap-1.5">
                  <Lock className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-400" />
                  <p
                    className={`text-xs text-slate-700 dark:text-slate-200 ${
                      isRevealed ? "" : "blur-sm select-none"
                    }`}
                  >
                    {rev.preview}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleReveal(rev.id)}
                    aria-label={isRevealed ? "Hide content" : "See content"}
                    title={isRevealed ? "Hide content" : "See content"}
                    className="ml-1 flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    {isRevealed ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                {rev.reviewedAt && (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                    {rev.userAction === "reclassified"
                      ? `Reclassified as ${rev.reclassifiedTo}`
                      : "Kept private"}
                  </p>
                )}
              </div>
              {!rev.reviewedAt && (
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                  <button
                    type="button"
                    onClick={() => keep(rev.packetId)}
                    disabled={busy === rev.packetId}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-300 dark:border-rose-700 bg-transparent text-rose-700 dark:text-rose-200 text-xs font-semibold px-2 py-1.5 hover:bg-rose-100 dark:hover:bg-rose-900/30 disabled:opacity-50"
                  >
                    {busy === rev.packetId ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">Keep private</span>
                  </button>
                  <select
                    aria-label="Reclassify as"
                    defaultValue=""
                    disabled={busy === rev.packetId}
                    onChange={(e) => reclassify(rev.packetId, e.target.value)}
                    className="text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1.5 text-slate-700 dark:text-slate-200 disabled:opacity-50"
                  >
                    <option value="" disabled>
                      Reclassify…
                    </option>
                    {RECLASSIFY_SCOPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
